// pages/CentralStockItemHistory.js
import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import {
    Box, Typography, Paper, Table, TableBody, TableCell, TableContainer, TableHead,
    TableRow, CircularProgress, Chip, Grid, Button, Badge, TablePagination,
    Autocomplete, TextField, IconButton, Collapse, Stack, Divider
} from '@mui/material';
import {
    ArrowBack, History, ExpandMore, ExpandLess, Add, Edit, Delete, SwapHoriz,
    FilterList, LocalShipping, TrendingUp, Build, Inventory
} from '@mui/icons-material';
import { DatePicker, LocalizationProvider } from '@mui/x-date-pickers';
import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFns';
import { useTranslation } from 'react-i18next';
import { toast } from 'react-toastify';
import centralStockAPI from '../../services/centralStockAPI';
import { branchAPI } from '../../services/api';
import NoPrivilege from '../../components/common/NoPrivilege';
import { useAuth } from '../../context/AuthContext';

// ── Log Details Component ─────────────────────────────────────────────────────
const LogDetails = ({ log, t }) => {
    const renderObjectDetails = (dataObject) => {
        const ignoreKeys = ['_id', '__v', 'createdAt', 'updatedAt', 'createdBy', 'updatedBy', 'wacHistory', 'dailySnapshots'];
        return (
            <Table size="small" aria-label="details">
                <TableBody>
                    {Object.entries(dataObject).map(([key, value]) => {
                        if (ignoreKeys.includes(key) || (typeof value === 'object' && value !== null)) return null;
                        return (
                            <TableRow key={key}>
                                <TableCell component="th" scope="row" sx={{ fontWeight: 'bold' }}>
                                    {t(`logDetails.${key}`, key)}
                                </TableCell>
                                <TableCell>{String(value)}</TableCell>
                            </TableRow>
                        );
                    })}
                </TableBody>
            </Table>
        );
    };

    switch (log.action) {
        case 'ORDER_DELIVERY_RECEIVED': {
            const details = log.orderDetails;
            return (
                <Box>
                    <Typography variant="subtitle2" fontWeight={600} mb={1}>
                        {t('logDetails.orderDeliveryTitle', 'Order Delivery Details')}
                    </Typography>
                    <Table size="small">
                        <TableBody>
                            <TableRow>
                                <TableCell sx={{ fontWeight: 'bold' }}>
                                    {t('logDetails.orderNumber', 'Order Number')}
                                </TableCell>
                                <TableCell>
                                    <Chip label={details?.orderNumber || '—'} size="small" color="primary" variant="outlined" />
                                </TableCell>
                            </TableRow>
                            <TableRow>
                                <TableCell sx={{ fontWeight: 'bold' }}>
                                    {t('logDetails.supplier', 'Supplier')}
                                </TableCell>
                                <TableCell>{details?.supplierName || '—'}</TableCell>
                            </TableRow>
                            <TableRow>
                                <TableCell sx={{ fontWeight: 'bold' }}>
                                    {t('logDetails.quantityDelivered', 'Quantity Delivered')}
                                </TableCell>
                                <TableCell>
                                    <Typography fontWeight={600} color="success.main">
                                        +{details?.quantityDelivered}
                                    </Typography>
                                </TableCell>
                            </TableRow>
                            <TableRow>
                                <TableCell sx={{ fontWeight: 'bold' }}>
                                    {t('logDetails.unitPrice', 'Unit Price')}
                                </TableCell>
                                <TableCell>{details?.unitPrice?.toFixed(2)} {t('common.currency')}</TableCell>
                            </TableRow>
                            <TableRow>
                                <TableCell sx={{ fontWeight: 'bold' }}>
                                    {t('logDetails.previousQuantity', 'Quantity Before')}
                                </TableCell>
                                <TableCell>{details?.previousQuantity}</TableCell>
                            </TableRow>
                            <TableRow>
                                <TableCell sx={{ fontWeight: 'bold' }}>
                                    {t('logDetails.newQuantity', 'Quantity After')}
                                </TableCell>
                                <TableCell>
                                    <Typography fontWeight={600}>{details?.newQuantity}</Typography>
                                </TableCell>
                            </TableRow>
                        </TableBody>
                    </Table>

                    <Divider sx={{ my: 1.5 }} />

                    <Typography variant="subtitle2" fontWeight={600} mb={1}>
                        {t('logDetails.wacUpdate', 'WAC (Weighted Average Cost) Update')}
                    </Typography>
                    <Table size="small">
                        <TableBody>
                            <TableRow>
                                <TableCell sx={{ fontWeight: 'bold' }}>
                                    {t('logDetails.previousAvgCost', 'Previous Avg Cost')}
                                </TableCell>
                                <TableCell>
                                    <Typography color="text.secondary">
                                        {details?.previousAvgCost?.toFixed(2)} {t('common.currency')}
                                    </Typography>
                                </TableCell>
                            </TableRow>
                            <TableRow>
                                <TableCell sx={{ fontWeight: 'bold' }}>
                                    {t('logDetails.newAvgCost', 'New Avg Cost')}
                                </TableCell>
                                <TableCell>
                                    <Typography fontWeight={600} color="primary.main">
                                        {details?.newAvgCost?.toFixed(2)} {t('common.currency')}
                                    </Typography>
                                </TableCell>
                            </TableRow>
                            <TableRow>
                                <TableCell sx={{ fontWeight: 'bold' }}>
                                    {t('logDetails.wacChange', 'WAC Change')}
                                </TableCell>
                                <TableCell>
                                    {(() => {
                                        const diff = (details?.newAvgCost || 0) - (details?.previousAvgCost || 0);
                                        const isUp = diff > 0;
                                        return (
                                            <Typography
                                                fontWeight={600}
                                                color={isUp ? 'error.main' : 'success.main'}
                                            >
                                                {isUp ? '+' : ''}{diff.toFixed(2)} {t('common.currency')}
                                                {' '}
                                                ({isUp ? '↑' : '↓'} {details?.previousAvgCost > 0
                                                    ? ((Math.abs(diff) / details.previousAvgCost) * 100).toFixed(1)
                                                    : '∞'}%)
                                            </Typography>
                                        );
                                    })()}
                                </TableCell>
                            </TableRow>
                        </TableBody>
                    </Table>
                </Box>
            );
        }

        case 'WAC_UPDATED': {
            const before = log.changes?.before || {};
            const after = log.changes?.after || {};
            return (
                <Table size="small">
                    <TableBody>
                        <TableRow>
                            <TableCell sx={{ fontWeight: 'bold' }}>
                                {t('logDetails.previousAvgCost', 'Previous Avg Cost')}
                            </TableCell>
                            <TableCell>
                                <Typography color="text.secondary">
                                    {before.averageCost?.toFixed(2)} {t('common.currency')}
                                </Typography>
                            </TableCell>
                        </TableRow>
                        <TableRow>
                            <TableCell sx={{ fontWeight: 'bold' }}>
                                {t('logDetails.newAvgCost', 'New Avg Cost')}
                            </TableCell>
                            <TableCell>
                                <Typography fontWeight={600} color="primary.main">
                                    {after.averageCost?.toFixed(2)} {t('common.currency')}
                                </Typography>
                            </TableCell>
                        </TableRow>
                    </TableBody>
                </Table>
            );
        }

        case 'PHYSICAL_COUNT_RECORDED': {
            const before = log.changes?.before || {};
            const after = log.changes?.after || {};
            return (
                <Table size="small">
                    <TableBody>
                        <TableRow>
                            <TableCell sx={{ fontWeight: 'bold' }}>
                                {t('logDetails.systemQuantity', 'System Quantity')}
                            </TableCell>
                            <TableCell>{before.quantity}</TableCell>
                        </TableRow>
                        <TableRow>
                            <TableCell sx={{ fontWeight: 'bold' }}>
                                {t('logDetails.countedQuantity', 'Counted Quantity')}
                            </TableCell>
                            <TableCell>
                                <Typography fontWeight={600}>{after.quantity}</Typography>
                            </TableCell>
                        </TableRow>
                        <TableRow>
                            <TableCell sx={{ fontWeight: 'bold' }}>
                                {t('logDetails.difference', 'Difference')}
                            </TableCell>
                            <TableCell>
                                <Typography
                                    fontWeight={600}
                                    color={(after.quantity - before.quantity) >= 0 ? 'success.main' : 'error.main'}
                                >
                                    {(after.quantity - before.quantity) >= 0 ? '+' : ''}
                                    {after.quantity - before.quantity}
                                </Typography>
                            </TableCell>
                        </TableRow>
                    </TableBody>
                </Table>
            );
        }

        case 'STOCK_TRANSFERRED_TO_BRANCH':
        case 'STOCK_TRANSFERRED_BETWEEN_BRANCHES': {
            const details = log.transferDetails;
            return (
                <Table size="small">
                    <TableBody>
                        <TableRow>
                            <TableCell sx={{ fontWeight: 'bold' }}>
                                {t('logDetails.quantityTransferred', 'Quantity Transferred')}
                            </TableCell>
                            <TableCell>{details?.quantity}</TableCell>
                        </TableRow>
                        {details?.wacAtTransfer !== undefined && (
                            <TableRow>
                                <TableCell sx={{ fontWeight: 'bold' }}>
                                    {t('logDetails.wacAtTransfer', 'WAC at Transfer')}
                                </TableCell>
                                <TableCell>{details.wacAtTransfer?.toFixed(2)} {t('common.currency')}</TableCell>
                            </TableRow>
                        )}
                        {/* Source info */}
                        {details?.source && (
                            <>
                                <TableRow>
                                    <TableCell sx={{ fontWeight: 'bold' }}>
                                        {t('logDetails.source', 'Source')}
                                    </TableCell>
                                    <TableCell>
                                        {details.source.type === 'central'
                                            ? t('logDetails.centralStock', 'Central Stock')
                                            : details.source.branchName || '—'}
                                    </TableCell>
                                </TableRow>
                                <TableRow>
                                    <TableCell sx={{ fontWeight: 'bold' }}>
                                        {t('logDetails.sourceStockBefore', 'Source Stock Before')}
                                    </TableCell>
                                    <TableCell>{details.source.stockBefore ?? details.centralStockBefore}</TableCell>
                                </TableRow>
                                <TableRow>
                                    <TableCell sx={{ fontWeight: 'bold' }}>
                                        {t('logDetails.sourceStockAfter', 'Source Stock After')}
                                    </TableCell>
                                    <TableCell>{details.source.stockAfter ?? details.centralStockAfter}</TableCell>
                                </TableRow>
                            </>
                        )}
                        {/* Destination info */}
                        {details?.destination && (
                            <>
                                <TableRow>
                                    <TableCell sx={{ fontWeight: 'bold' }}>
                                        {t('logDetails.destination', 'Destination')}
                                    </TableCell>
                                    <TableCell>{details.destination.branchName || details.branch?.branchName || '—'}</TableCell>
                                </TableRow>
                                {details.destination.stockBefore !== undefined && (
                                    <TableRow>
                                        <TableCell sx={{ fontWeight: 'bold' }}>
                                            {t('logDetails.destStockBefore', 'Dest. Stock Before')}
                                        </TableCell>
                                        <TableCell>{details.destination.stockBefore}</TableCell>
                                    </TableRow>
                                )}
                                {details.destination.stockAfter !== undefined && (
                                    <TableRow>
                                        <TableCell sx={{ fontWeight: 'bold' }}>
                                            {t('logDetails.destStockAfter', 'Dest. Stock After')}
                                        </TableCell>
                                        <TableCell>{details.destination.stockAfter}</TableCell>
                                    </TableRow>
                                )}
                            </>
                        )}
                        {/* Legacy fallback */}
                        {!details?.source && details?.branch && (
                            <>
                                <TableRow>
                                    <TableCell sx={{ fontWeight: 'bold' }}>{t('logDetails.branchName', 'Branch Name')}</TableCell>
                                    <TableCell>{details.branch.branchName}</TableCell>
                                </TableRow>
                                <TableRow>
                                    <TableCell sx={{ fontWeight: 'bold' }}>{t('logDetails.stockBefore', 'Stock Before')}</TableCell>
                                    <TableCell>{details.centralStockBefore}</TableCell>
                                </TableRow>
                                <TableRow>
                                    <TableCell sx={{ fontWeight: 'bold' }}>{t('logDetails.stockAfter', 'Stock After')}</TableCell>
                                    <TableCell>{details.centralStockAfter}</TableCell>
                                </TableRow>
                            </>
                        )}
                    </TableBody>
                </Table>
            );
        }

        case 'CENTRAL_STOCK_UPDATED': {
            const before = log.changes?.before || {};
            const after = log.changes?.after || {};
            const ignoreKeys = ['_id', '__v', 'createdAt', 'updatedAt', 'createdBy', 'updatedBy', 'wacHistory', 'dailySnapshots'];
            const allKeys = [...new Set([...Object.keys(before), ...Object.keys(after)])];
            return (
                <Table size="small">
                    <TableHead>
                        <TableRow>
                            <TableCell>{t('logDetails.field', 'Field')}</TableCell>
                            <TableCell>{t('logDetails.before', 'Before')}</TableCell>
                            <TableCell>{t('logDetails.after', 'After')}</TableCell>
                        </TableRow>
                    </TableHead>
                    <TableBody>
                        {allKeys.map(key => {
                            if (ignoreKeys.includes(key) || JSON.stringify(before[key]) === JSON.stringify(after[key])) return null;
                            return (
                                <TableRow key={key}>
                                    <TableCell component="th" scope="row" sx={{ fontWeight: 'bold' }}>
                                        {t(`logDetails.${key}`, key)}
                                    </TableCell>
                                    <TableCell sx={{ color: 'error.main' }}>{String(before[key] ?? 'N/A')}</TableCell>
                                    <TableCell sx={{ color: 'success.main', fontWeight: 'bold' }}>{String(after[key] ?? 'N/A')}</TableCell>
                                </TableRow>
                            );
                        })}
                    </TableBody>
                </Table>
            );
        }

        case 'CENTRAL_STOCK_CREATED':
            return renderObjectDetails(log.changes?.after || {});

        case 'CENTRAL_STOCK_DELETED':
            return renderObjectDetails(log.changes?.before || {});

        default:
            return <pre style={{ fontSize: '0.75rem', overflow: 'auto' }}>{JSON.stringify(log, null, 2)}</pre>;
    }
};

// ── Item Info Card ────────────────────────────────────────────────────────────
const ItemInfoCard = ({ itemInfo, t }) => {
    if (!itemInfo) return null;

    return (
        <Paper elevation={1} sx={{ p: 2, mb: 3 }}>
            <Grid container spacing={2}>
                <Grid item xs={6} sm={3}>
                    <Typography variant="caption" color="text.secondary">
                        {t('centralStock.quantity')}
                    </Typography>
                    <Typography variant="h6" fontWeight={600}>
                        {itemInfo.quantity} {itemInfo.unit}
                    </Typography>
                </Grid>
                <Grid item xs={6} sm={3}>
                    <Typography variant="caption" color="text.secondary">
                        {t('centralStock.avgCost', 'Average Cost (WAC)')}
                    </Typography>
                    <Typography variant="h6" fontWeight={600} color="primary.main">
                        {itemInfo.averageCost?.toFixed(2) || '0.00'} {t('common.currency')}
                    </Typography>
                </Grid>
                <Grid item xs={6} sm={3}>
                    <Typography variant="caption" color="text.secondary">
                        {t('centralStock.stockValue', 'Stock Value')}
                    </Typography>
                    <Typography variant="h6" fontWeight={600}>
                        {((itemInfo.quantity || 0) * (itemInfo.averageCost || 0)).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })} {t('common.currency')}
                    </Typography>
                </Grid>
                <Grid item xs={6} sm={3}>
                    <Typography variant="caption" color="text.secondary">
                        {t('centralStock.wacChanges', 'WAC Changes')}
                    </Typography>
                    <Typography variant="h6" fontWeight={600}>
                        {itemInfo.wacHistory?.length || 0}
                    </Typography>
                </Grid>
            </Grid>
        </Paper>
    );
};

// ── Main Component ────────────────────────────────────────────────────────────
const CentralStockItemHistory = () => {
    const { t } = useTranslation();
    const { itemId } = useParams();
    const navigate = useNavigate();
    const { user: authUser, isSuperAdmin } = useAuth();

    const [auditLogs, setAuditLogs] = useState([]);
    const [itemInfo, setItemInfo] = useState(null);
    const [loading, setLoading] = useState(true);
    const [page, setPage] = useState(0);
    const [rowsPerPage, setRowsPerPage] = useState(10);
    const [totalCount, setTotalCount] = useState(0);

    const [filters, setFilters] = useState({ dateFrom: null, dateTo: null, branch: null, user: null, action: null });
    const [branches, setBranches] = useState([]);
    const [expandedRows, setExpandedRows] = useState(new Set());

    const canView = isSuperAdmin || authUser?.privileges?.some(p => p.module === 'centralStock' && p.operations.includes('view'));

    // ── Action filter options ─────────────────────────────────────────────
    const ACTION_OPTIONS = [
        { value: 'CENTRAL_STOCK_CREATED', label: t('auditLog.CENTRAL_STOCK_CREATED', 'Created') },
        { value: 'CENTRAL_STOCK_UPDATED', label: t('auditLog.CENTRAL_STOCK_UPDATED', 'Updated') },
        { value: 'CENTRAL_STOCK_DELETED', label: t('auditLog.CENTRAL_STOCK_DELETED', 'Deleted') },
        { value: 'STOCK_TRANSFERRED_TO_BRANCH', label: t('auditLog.STOCK_TRANSFERRED_TO_BRANCH', 'Transfer to Branch') },
        { value: 'STOCK_TRANSFERRED_BETWEEN_BRANCHES', label: t('auditLog.STOCK_TRANSFERRED_BETWEEN_BRANCHES', 'Branch to Branch') },
        { value: 'ORDER_DELIVERY_RECEIVED', label: t('auditLog.ORDER_DELIVERY_RECEIVED', 'Order Delivery') },
        { value: 'WAC_UPDATED', label: t('auditLog.WAC_UPDATED', 'WAC Updated') },
        { value: 'PHYSICAL_COUNT_RECORDED', label: t('auditLog.PHYSICAL_COUNT_RECORDED', 'Physical Count') },
    ];

    const fetchAuditLogs = useCallback(async () => {
        if (!canView) return;
        setLoading(true);
        try {
            const params = {
                page: page + 1,
                limit: rowsPerPage,
                ...(filters.branch && { branch: filters.branch._id }),
                ...(filters.user && { user: filters.user._id }),
                ...(filters.action && { action: filters.action.value }),
                ...(filters.dateFrom && { dateFrom: filters.dateFrom.toISOString() }),
                ...(filters.dateTo && { dateTo: filters.dateTo.toISOString() }),
            };
            const response = await centralStockAPI.getAuditLogsForStock(itemId, params);
            setAuditLogs(response.data.data);
            setTotalCount(response.data.pagination.total);
        } catch (error) {
            toast.error(t('common.fetchError'));
        } finally {
            setLoading(false);
        }
    }, [itemId, canView, t, page, rowsPerPage, filters]);

    useEffect(() => {
        fetchAuditLogs();
    }, [fetchAuditLogs]);

    useEffect(() => {
        const fetchFilterData = async () => {
            try {
                const branchRes = await branchAPI.getActiveBranches();
                setBranches(branchRes.data?.data || []);
            } catch (error) {
                toast.error('Failed to load branch options');
            }
        };
        if (canView) {
            fetchFilterData();
            centralStockAPI.getById(itemId)
                .then(res => setItemInfo(res.data.data))
                .catch(() => setItemInfo(null));
        }
    }, [canView, itemId]);

    const uniqueUsersForFilter = useMemo(() => {
        const userMap = new Map();
        auditLogs.forEach(log => {
            const user = log.performedBy?.userId;
            if (user?._id && !userMap.has(user._id)) {
                userMap.set(user._id, user);
            }
        });
        if (filters.user && !userMap.has(filters.user._id)) {
            userMap.set(filters.user._id, filters.user);
        }
        return Array.from(userMap.values());
    }, [auditLogs, filters.user]);

    const handleFilterChange = (key, value) => {
        setPage(0);
        setFilters(prev => ({ ...prev, [key]: value }));
    };

    const handleClearFilters = () => {
        setPage(0);
        setFilters({ dateFrom: null, dateTo: null, branch: null, user: null, action: null });
    };

    const handleExpandRow = (logId) => {
        const newExpanded = new Set(expandedRows);
        if (newExpanded.has(logId)) newExpanded.delete(logId); else newExpanded.add(logId);
        setExpandedRows(newExpanded);
    };

    const getActionIcon = (action) => {
        switch (action) {
            case 'CENTRAL_STOCK_CREATED': return <Add fontSize="small" />;
            case 'CENTRAL_STOCK_UPDATED': return <Edit fontSize="small" />;
            case 'CENTRAL_STOCK_DELETED': return <Delete fontSize="small" />;
            case 'STOCK_TRANSFERRED_TO_BRANCH':
            case 'STOCK_TRANSFERRED_BETWEEN_BRANCHES':
                return <SwapHoriz fontSize="small" />;
            case 'ORDER_DELIVERY_RECEIVED': return <LocalShipping fontSize="small" />;
            case 'WAC_UPDATED': return <TrendingUp fontSize="small" />;
            case 'PHYSICAL_COUNT_RECORDED': return <Inventory fontSize="small" />;
            case 'MANUAL_ADJUSTMENT': return <Build fontSize="small" />;
            default: return <History fontSize="small" />;
        }
    };

    const getActionColor = (action) => {
        switch (action) {
            case 'CENTRAL_STOCK_CREATED': return 'success';
            case 'CENTRAL_STOCK_UPDATED': return 'info';
            case 'CENTRAL_STOCK_DELETED': return 'error';
            case 'STOCK_TRANSFERRED_TO_BRANCH':
            case 'STOCK_TRANSFERRED_BETWEEN_BRANCHES':
                return 'warning';
            case 'ORDER_DELIVERY_RECEIVED': return 'success';
            case 'WAC_UPDATED': return 'primary';
            case 'PHYSICAL_COUNT_RECORDED': return 'secondary';
            default: return 'default';
        }
    };

    const renderSummary = (log) => {
        switch (log.action) {
            case 'ORDER_DELIVERY_RECEIVED': {
                const d = log.orderDetails;
                return t('logDetails.orderDeliverySummary',
                    `Received ${d?.quantityDelivered || 0} units from order ${d?.orderNumber || '—'} (${d?.supplierName || 'supplier'}). WAC: ${d?.previousAvgCost?.toFixed(2) || '0'} → ${d?.newAvgCost?.toFixed(2) || '0'}`,
                    {
                        qty: d?.quantityDelivered || 0,
                        orderNumber: d?.orderNumber || '—',
                        supplier: d?.supplierName || 'supplier',
                        prevWac: d?.previousAvgCost?.toFixed(2) || '0',
                        newWac: d?.newAvgCost?.toFixed(2) || '0'
                    }
                );
            }
            case 'WAC_UPDATED': {
                const before = log.changes?.before?.averageCost;
                const after = log.changes?.after?.averageCost;
                return t('logDetails.wacUpdateSummary',
                    `WAC changed: ${before?.toFixed(2) || '0'} → ${after?.toFixed(2) || '0'}`,
                    { prevWac: before?.toFixed(2) || '0', newWac: after?.toFixed(2) || '0' }
                );
            }
            case 'PHYSICAL_COUNT_RECORDED': {
                const before = log.changes?.before?.quantity;
                const after = log.changes?.after?.quantity;
                return t('logDetails.physicalCountSummary',
                    `Physical count: ${before} → ${after} (diff: ${(after - before) >= 0 ? '+' : ''}${after - before})`,
                    { before, after, diff: after - before }
                );
            }
            case 'STOCK_TRANSFERRED_TO_BRANCH':
                return t('logDetails.transfer', {
                    quantity: log.transferDetails?.quantity,
                    branchName: log.transferDetails?.destination?.branchName || log.transferDetails?.branch?.branchName || 'a branch'
                });
            case 'STOCK_TRANSFERRED_BETWEEN_BRANCHES':
                return t('logDetails.branchTransfer',
                    `Transferred ${log.transferDetails?.quantity} from ${log.transferDetails?.source?.branchName || '—'} to ${log.transferDetails?.destination?.branchName || '—'}`,
                    {
                        quantity: log.transferDetails?.quantity,
                        source: log.transferDetails?.source?.branchName || '—',
                        destination: log.transferDetails?.destination?.branchName || '—'
                    }
                );
            case 'CENTRAL_STOCK_UPDATED':
                return t('logDetails.update', 'Updated item properties');
            case 'CENTRAL_STOCK_CREATED':
                return t('logDetails.create', { quantity: log.changes?.after?.quantity || 0 });
            case 'CENTRAL_STOCK_DELETED':
                return t('logDetails.delete', 'Deleted item from stock');
            default:
                return t('logDetails.noSummary', 'No summary available');
        }
    };

    if (!canView) return <NoPrivilege message={t('common.noPermission')} />;

    return (
        <Box sx={{ p: 3 }}>
            {/* Header */}
            <Paper elevation={1} sx={{ p: 3, mb: 3 }}>
                <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 2 }}>
                    <Typography variant="h4" component="h1" display="flex" alignItems="center" gap={2}>
                        {t('history.itemHistory')}
                        <Badge badgeContent={totalCount} color="primary">
                            <History color="action" />
                        </Badge>
                    </Typography>
                    <Button endIcon={<ArrowBack />} onClick={() => navigate('/central-stock')} variant="outlined">
                        {t('history.back')}
                    </Button>
                </Box>
                <Typography variant="h6" color="text.secondary">
                    {itemInfo?.name || t('common.loading')}
                </Typography>
            </Paper>

            {/* Item Info Card — shows current WAC, qty, stock value */}
            <ItemInfoCard itemInfo={itemInfo} t={t} />

            {/* Filters */}
            <Paper sx={{ p: 2, mb: 3 }}>
                <Stack direction="row" alignItems="center" gap={2} mb={2}>
                    <FilterList />
                    <Typography variant="h6">{t('filters')}</Typography>
                </Stack>
                <Grid container spacing={2}>
                    <Grid item xs={12} sm={6} md={2.4}>
                        <LocalizationProvider dateAdapter={AdapterDateFns}>
                            <DatePicker
                                label={t('common.dateFrom')}
                                value={filters.dateFrom}
                                onChange={(date) => handleFilterChange('dateFrom', date)}
                                slotProps={{ textField: { fullWidth: true, size: 'small' } }}
                            />
                        </LocalizationProvider>
                    </Grid>
                    <Grid item xs={12} sm={6} md={2.4}>
                        <LocalizationProvider dateAdapter={AdapterDateFns}>
                            <DatePicker
                                label={t('common.dateTo')}
                                value={filters.dateTo}
                                onChange={(date) => handleFilterChange('dateTo', date)}
                                slotProps={{ textField: { fullWidth: true, size: 'small' } }}
                            />
                        </LocalizationProvider>
                    </Grid>
                    <Grid item xs={12} sm={6} md={2.4}>
                        <Autocomplete
                            options={ACTION_OPTIONS}
                            getOptionLabel={(option) => option.label}
                            isOptionEqualToValue={(option, value) => option.value === value.value}
                            value={filters.action}
                            onChange={(e, val) => handleFilterChange('action', val)}
                            renderInput={(params) => (
                                <TextField {...params} label={t('history.actionFilter', 'Action Type')} size="small" />
                            )}
                        />
                    </Grid>
                    <Grid item xs={12} sm={6} md={2.4}>
                        <Autocomplete
                            options={branches}
                            getOptionLabel={(option) => option.name}
                            isOptionEqualToValue={(option, value) => option._id === value._id}
                            value={filters.branch}
                            onChange={(e, val) => handleFilterChange('branch', val)}
                            renderInput={(params) => (
                                <TextField {...params} label={t('history.branch')} size="small" />
                            )}
                        />
                    </Grid>
                    <Grid item xs={12} sm={6} md={2.4}>
                        <Autocomplete
                            options={uniqueUsersForFilter}
                            getOptionLabel={(option) => option.username || ''}
                            isOptionEqualToValue={(option, value) => option._id === value._id}
                            value={filters.user}
                            onChange={(event, newValue) => handleFilterChange('user', newValue)}
                            renderInput={(params) => (
                                <TextField {...params} label={t('history.performedBy', 'Performed By')} size="small" />
                            )}
                        />
                    </Grid>
                    <Grid item xs={12} display="flex" justifyContent="flex-end">
                        <Button onClick={handleClearFilters} variant="outlined">
                            {t('common.clearFilters')}
                        </Button>
                    </Grid>
                </Grid>
            </Paper>

            {/* Audit Logs Table */}
            <Paper>
                <TableContainer>
                    <Table>
                        <TableHead>
                            <TableRow>
                                <TableCell>{t('history.action')}</TableCell>
                                <TableCell>{t('history.user')}</TableCell>
                                <TableCell>{t('history.dateTime')}</TableCell>
                                <TableCell>{t('history.summary')}</TableCell>
                                <TableCell align="center">{t('history.details')}</TableCell>
                            </TableRow>
                        </TableHead>
                        <TableBody>
                            {loading ? (
                                <TableRow>
                                    <TableCell colSpan={5} align="center" sx={{ py: 5 }}>
                                        <CircularProgress />
                                    </TableCell>
                                </TableRow>
                            ) : auditLogs.length === 0 ? (
                                <TableRow>
                                    <TableCell colSpan={5} align="center" sx={{ py: 5 }}>
                                        <Typography>{t('history.noHistoryFound')}</Typography>
                                    </TableCell>
                                </TableRow>
                            ) : auditLogs.map((log) => (
                                <React.Fragment key={log._id}>
                                    <TableRow hover sx={{ '& > *': { borderBottom: 'unset' } }}>
                                        <TableCell>
                                            <Chip
                                                icon={getActionIcon(log.action)}
                                                label={t(`auditLog.${log.action}`, log.action)}
                                                color={getActionColor(log.action)}
                                                size="small"
                                                variant="outlined"
                                            />
                                        </TableCell>
                                        <TableCell>{log.performedBy?.userName || t('common.unknown')}</TableCell>
                                        <TableCell>{new Date(log.createdAt).toLocaleString()}</TableCell>
                                        <TableCell>
                                            <Typography variant="body2" sx={{ maxWidth: 400 }}>
                                                {renderSummary(log)}
                                            </Typography>
                                        </TableCell>
                                        <TableCell align="center">
                                            <IconButton onClick={() => handleExpandRow(log._id)} size="small">
                                                {expandedRows.has(log._id) ? <ExpandLess /> : <ExpandMore />}
                                            </IconButton>
                                        </TableCell>
                                    </TableRow>
                                    <TableRow>
                                        <TableCell colSpan={5} sx={{ p: 0 }}>
                                            <Collapse in={expandedRows.has(log._id)} timeout="auto" unmountOnExit>
                                                <Box sx={{ margin: 1, padding: 2, backgroundColor: 'action.hover', borderRadius: 1 }}>
                                                    <LogDetails log={log} t={t} />
                                                </Box>
                                            </Collapse>
                                        </TableCell>
                                    </TableRow>
                                </React.Fragment>
                            ))}
                        </TableBody>
                    </Table>
                </TableContainer>
                <TablePagination
                    rowsPerPageOptions={[5, 10, 25, 50]}
                    component="div"
                    count={totalCount}
                    rowsPerPage={rowsPerPage}
                    page={page}
                    onPageChange={(e, newPage) => setPage(newPage)}
                    onRowsPerPageChange={(e) => { setRowsPerPage(parseInt(e.target.value, 10)); setPage(0); }}
                />
            </Paper>
        </Box>
    );
};

export default CentralStockItemHistory;